import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

const PRESENCE_INTERVAL = 2 * 60 * 1000; // 2 minutes
const VISIT_TRACKED_KEY = 'ekselencia_visit_tracked';

export const PresenceTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  const lastPresenceRef = useRef<number>(0);
  const lastRouteRef = useRef<string>("");

  useEffect(() => {
    const currentRoute = location.pathname;

    // --- 1. Track visitor ONCE per browser session (sessionStorage) ---
    try {
      const alreadyTracked = sessionStorage.getItem(VISIT_TRACKED_KEY);
      if (!alreadyTracked) {
        sessionStorage.setItem(VISIT_TRACKED_KEY, '1');
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ route: currentRoute, user_id: user?.id || null }),
        }).catch(() => {});
      }
    } catch {
      // sessionStorage blocked (e.g. iframe sandbox) — skip tracking silently
    }

    // --- 2. User presence updates (authenticated users only, throttled) ---
    const updatePresence = async () => {
      if (!user?.id) return;
      const now = Date.now();
      const routeChanged = currentRoute !== lastRouteRef.current;
      const timeElapsed = now - lastPresenceRef.current > PRESENCE_INTERVAL;
      if (!routeChanged && !timeElapsed && lastPresenceRef.current > 0) return;

      lastPresenceRef.current = now;
      lastRouteRef.current = currentRoute;

      try {
        await supabase
          .from("user_presence")
          .upsert(
            {
              user_id: user.id,
              last_seen_at: new Date().toISOString(),
              is_online: true,
              current_route: currentRoute,
            },
            { onConflict: "user_id" }
          );
      } catch {
        // Silently fail
      }
    };

    updatePresence();
    const intervalId = setInterval(updatePresence, PRESENCE_INTERVAL);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        updatePresence();
      } else if (user?.id) {
        // Set offline when tab is hidden
        supabase
          .from("user_presence")
          .update({ is_online: false })
          .eq("user_id", user.id)
          .then();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      // Set offline on unmount if authenticated
      if (user?.id) {
        supabase
          .from("user_presence")
          .update({ is_online: false })
          .eq("user_id", user.id)
          .then();
      }
    };
  }, [location.pathname, user?.id]);

  return null;
};
